import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {format} from 'date-fns'
import * as XLSX from 'xlsx'
import * as FileSaver from 'file-saver'
import {v4 as uuidv4} from "uuid";
import {
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileArchive
 } from 'react-icons/fa';
import { AVATAR_COLORS } from 'app_config';

dayjs.extend(duration);

export const isIterableArray = array => Array.isArray(array) && !!array.length;

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540
};

export const getItemFromStore = (key, defaultValue, store = localStorage) => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : JSON.parse(store.getItem(key));
  } catch {
    return store.getItem(key) || defaultValue;
  }
};

export const setItemToStore = (key, payload, store = localStorage) =>
  store.setItem(key, payload);

export const getStoreSpace = (store = localStorage) =>
  parseFloat(
    (
      escape(encodeURIComponent(JSON.stringify(store))).length /
      (1024 * 1024)
    ).toFixed(2)
  );

//===============================
// Cookie
//===============================
export const getCookieValue = name => {
  const value = document.cookie.match(
    '(^|[^;]+)\\s*' + name + '\\s*=\\s*([^;]+)'
  );
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = '; expires=' + date.toUTCString();
  document.cookie = name + '=' + value + expires + '; path=/';
};

export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + 'B'
    : // Six Zeroes for Millions
    Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + 'M'
    : // Three Zeroes for Thousands
    Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + 'K'
    : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// Colors
//===============================
export const hexToRgb = hexValue => {
  let hex;
  hexValue.indexOf('#') === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
  );
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
    : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) =>
  `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
  '#2c7be5',
  '#00d97e',
  '#e63757',
  '#39afd1',
  '#fd7e14',
  '#02a8b5',
  '#727cf5',
  '#6b5eae',
  '#ff679b',
  '#f6c343'
];

export const themeColors = {
  primary: '#2c7be5',
  secondary: '#748194',
  success: '#00d27a',
  info: '#27bcfd',
  warning: '#f5803e',
  danger: '#e63757',
  light: '#f9fafd',
  dark: '#0b1727'
};

export const grays = {
  white: '#fff',
  100: '#f9fafd',
  200: '#edf2f9',
  300: '#d8e2ef',
  400: '#b6c1d2',
  500: '#9da9bb',
  600: '#748194',
  700: '#5e6e82',
  800: '#4d5969',
  900: '#344050',
  1000: '#232e3c',
  1100: '#0b1727',
  black: '#000'
};

export const darkGrays = {
  white: '#fff',
  1100: '#f9fafd',
  1000: '#edf2f9',
  900: '#d8e2ef',
  800: '#b6c1d2',
  700: '#9da9bb',
  600: '#748194',
  500: '#5e6e82',
  400: '#4d5969',
  300: '#344050',
  200: '#232e3c',
  100: '#0b1727',
  black: '#000'
};

export const getGrays = isDark => (isDark ? darkGrays : grays);

export const rgbColors = colors.map(color => rgbColor(color));
export const rgbaColors = colors.map(color => rgbaColor(color));

// Resolves palette names ('primary', 'gray-300', ...) from the JS palettes
// above. This used to read --falcon-* CSS variables, but those were deleted
// with the Bootstrap/Falcon theme, so every call silently returned '' and
// charts fell back to library defaults.
export const getColor = name => {
  if (name.startsWith('gray-')) {
    return grays[name.slice('gray-'.length)] || '';
  }
  return themeColors[name] || grays[name] || '';
};

//===============================

// Echarts
//===============================
export const getPosition = (pos, params, dom, rect, size) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2
});
//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) =>
  (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find(item => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);
export const getSubtotal = items =>
  items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);
export const getDiscountPrice = (total, discount) =>
  total - total * (discount / 100);

export const getProductsQuantity = products =>
  products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = str => {
  if (!str) return ''; // Check for undefined, null, or empty string
  return (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, ' ');
};

export const camelize = str => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const dashed = str => {
  return str.toLowerCase().replaceAll(' ', '-');
};

// Escape user-supplied text before interpolating it into markup that is
// rendered as HTML (e.g. `confirmAlert` messages).
export const escapeHtml = str =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

//routes helper

export const flatRoutes = childrens => {
  const allChilds = [];

  const flatChild = childrens => {
    childrens.forEach(child => {
      if (child.children) {
        flatChild(child.children);
      } else {
        allChilds.push(child);
      }
    });
  };
  flatChild(childrens);

  return allChilds;
};

export const getFlatRoutes = children =>
  children.reduce(
    (acc, val) => {
      if (val.children) {
        return {
          ...acc,
          [camelize(val.name)]: flatRoutes(val.children)
        };
      } else {
        return {
          ...acc,
          unTitled: [...acc.unTitled, val]
        };
      }
    },
    { unTitled: [] }
  );

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map(route => {
    if (route.children) {
      return route.children.map(item => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = pageName => {
  return window.location.pathname.split('/').slice(-1)[0] === pageName;
};

export const copyToClipBoard = textFieldRef => {
  const textField = textFieldRef.current;
  textField.focus();
  textField.select();
  document.execCommand('copy');
};


export const pagination = (currentPage, size) => {
  const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  let prev = currentPage - 1 - Math.floor(size / 2);

  if (currentPage - 1 - Math.floor(size / 2) < 0) {
    prev = 0;
  }
  if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
    prev = pages.length - size;
  }
  const next = prev + size;

  return pages.slice(prev, next);
};

export const tooltipFormatter = params => {
  let tooltipItem = ``;
  params.forEach(el => {
    tooltipItem =
      tooltipItem +
      `<div class='tw-ml-1'> 
        <h6 class="tw-text-slate-700"><span class="fas fa-circle tw-mr-1 tw-text-[11px]" style="color:${
          el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${
        typeof el.value === 'object' ? el.value[1] : el.value
      }
        </h6>
      </div>`;
  });
  return `<div>
            <p class='tw-mb-2 tw-text-slate-600'>
              ${
                dayjs(params[0].axisValue).isValid()
                  ? dayjs(params[0].axisValue).format('MMMM DD')
                  : params[0].axisValue
              }
            </p>
            ${tooltipItem}
          </div>`;
};

export const addIdField = items => {
  return items.map((item, index) => ({
    id: index + 1,
    ...item
  }));
};

// get file size

export const getSize = size => {
  if (size < 1024) {
    return `${size} Byte`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/* Get A Random Number */
export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min) + min);
};

/* get Dates between */

export const getDates = (
  startDate,
  endDate,
  interval = 1000 * 60 * 60 * 24
) => {
  const duration = endDate - startDate;
  const steps = duration / interval;
  return Array.from(
    { length: steps + 1 },
    (v, i) => new Date(startDate.valueOf() + interval * i)
  );
};

/* Get Past Dates */
export const getPastDates = duration => {
  let days;

  switch (duration) {
    case 'week':
      days = 7;
      break;
    case 'month':
      days = 30;
      break;
    case 'year':
      days = 365;
      break;

    default:
      days = duration;
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

// Add id to items in array
export const addId = items =>
  items.map((item, index) => ({
    id: index + 1,
    ...item
  }));

//
export const getTimeDuration = (startDate, endDate, format = '') => {
  return dayjs.duration(endDate.diff(startDate)).format(format);
};

// Get Percentage
export const getPercentage = (number, percent) => {
  return (Number(number) / 100) * Number(percent);
};

//get chunk from array
export const chunk = (arr, chunkSize = 1, cache = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};

// Slugify text
export const slugifyText = str =>
  str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

export const joinWithArray = obj => {
  if (Array.isArray(obj)) {
    return obj.join(", ")
  }
  return obj
}

export const objectSerializer = (object) => {
  if (object === undefined)
    return

  let arrayObj = []
  if (Array.isArray(object)){
    object.map(obj => arrayObj.push({
      id: obj.id,  // ← ADD ID from JSONAPI response
      ...obj.attributes
    }))
  }
  else {
    arrayObj.push({
      id: object.id,  // ← ADD ID from JSONAPI response
      ...object.attributes
    })
  }

  return arrayObj
}

export const formatNumber = (number, currency="USD", numberDecimal=2) => {
  if (!number) return 0
  let formattedNumber = number.toString();
  
  if (currency === "USD") {
    formattedNumber = parseFloat(formattedNumber).toFixed(numberDecimal);
  }
  
  const parts = formattedNumber.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return currency === "VND" ? parts[0] : parts.join(".")
}

export const formatNumberCurrency = (number, currency="USD") => {
  const value = formatNumber(number, currency)
  return `${value} ${currency}`
}

export const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

export const formatBookingDate = (date) => {
  if (!date) return
  return format(new Date(date), "dd-MMM-yyyy h:mm aa")
}

export const formatDate = (date, formatStr="dd-MMM-yyyy") => {
  if (!date || date === undefined) return '';
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return '';
  return format(parsedDate, formatStr);
}

// ── CRM Report formatters ─────────────────────────────────────────────────────
// Parse YYYY-MM-DD strings without creating a Date object to avoid timezone
// offset issues (new Date('2026-06-01') is treated as UTC midnight and can shift
// a day backwards in GMT+ zones).

const _REPORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Converts a YYYY-MM-DD string → dd-MMM-yyyy (e.g. "09-Jun-2026"). */
export const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10);
  const [yr, mo, dy] = s.split('-');
  if (!yr || !mo || !dy) return s;
  return `${dy}-${_REPORT_MONTHS[parseInt(mo, 10) - 1] || mo}-${yr}`;
};

/** Formats a numeric value to 2 decimal places; returns '—' for null/NaN. */
export const fmtNum = (v) =>
  v != null && !Number.isNaN(Number(v))
    ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

/** Same as fmtNum — explicit alias for weight fields. */
export const fmtWeight = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const removeUnderlineString = (string) => {
  if (!string) return
  return string.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); //reject_and_waiting_adjust_drawing
}

export const ExportToExcel = (data, fileName) => {
  const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
  const fileExtension = ".xlsx"

  if (data.length === 0) return

  // Helper function to check if a value is an array
  const isArray = (value) => Array.isArray(value);

  // Helper function to process array values
  const processArrayValue = (value) => {
    if (isArray(value)) {
      return value.join(', '); // Convert the array to a comma-separated string
    }
    return value;
  };

  // Process the data before exporting
  const processedData = data.map((item) => {
    const processedItem = {};
    for (const key in item) {
      const value = item[key];
      processedItem[key] = processArrayValue(value);
    }
    return processedItem;
  });

  const ws = XLSX.utils.json_to_sheet(processedData)
  const wb = { Sheets: {'data': ws}, SheetNames: ['data']}
  const excelBuffer = XLSX.write(wb, {bookType: 'xlsx', type: 'array'})
  const dataBlob = new Blob([excelBuffer], {type: fileType})
  FileSaver.saveAs(dataBlob, fileName + fileExtension)
  
}

export const formatNumberWord = (number) => {
  if (number === undefined)
    return 0;
  
  if (number < 1000) 
    return number

  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const suffixNum = Math.floor((Math.log10(number) / 3));
  const shortNumber = (number / Math.pow(1000, suffixNum)).toFixed(1);

  return shortNumber.toString() + suffixes[suffixNum];
};

export const monthNameMap = {
  '1': 'Jan',
  '2': 'Feb',
  '3': 'Mar',
  '4': 'Apr',
  '5': 'May',
  '6': 'Jun',
  '7': 'Jul',
  '8': 'Aug',
  '9': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec'
};

export const monthNameMapDecimal = {
  '1.0': 'Jan',
  '2.0': 'Feb',
  '3.0': 'Mar',
  '4.0': 'Apr',
  '5.0': 'May',
  '6.0': 'Jun',
  '7.0': 'Jul',
  '8.0': 'Aug',
  '9.0': 'Sep',
  '10.0': 'Oct',
  '11.0': 'Nov',
  '12.0': 'Dec'
};

export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0;
};

export const formatDateToWord = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const currentDate = new Date(date);
  const diff = now - currentDate;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return "Just now";
  } else if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  } else if (days === 1) {
    return "yesterday";
  } else {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }
};

export const formatString = (str) => {
  if (!str) {
    return ''; // or some default value
  }
  return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export const adjustToNoon = (date) => {
  if (!date) return null; // Handle null date
  const adjustedDate = new Date(date);
  adjustedDate.setHours(12, 0, 0, 0); // Set to noon
  return adjustedDate;
};

export const generateUniqueId = (existingIds) => {
  let newId;
  do {
    newId = uuidv4();
  } while (existingIds.includes(newId));
  return newId;
};

export const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getFileIcon = (fileName) => {
  if (!fileName) return FaFile;
  
  const ext = fileName.split('.').pop().toLowerCase();
  
  const iconMap = {
    pdf: FaFilePdf,
    doc: FaFileWord,
    docx: FaFileWord,
    xls: FaFileExcel,
    xlsx: FaFileExcel,
    jpg: FaFileImage,
    jpeg: FaFileImage,
    png: FaFileImage,
    gif: FaFileImage,
    svg: FaFileImage,
    webp: FaFileImage,
    zip: FaFileArchive,
    rar: FaFileArchive,
    '7z': FaFileArchive
  };
  
  return iconMap[ext] || FaFile;
};

export const getNameColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};